import type { MapDocument } from "../document/types.ts";
import type { AppearanceTable } from "./behaviour.ts";
import { inChunk, loweredFeet, presenceIndex, type ChunkLandmark } from "./chunk.ts";
import type { CompileContext } from "./context.ts";

/**
 * The landmark pass (ADR-100 decisions 54 to 57): the things a map picture draws, listed into the client chunk
 * that holds their feet.
 */

const SIXTEENTHS = 16;

/**
 * The types whose things a picture never draws.
 *
 * @param context The compile context.
 * @returns The type ids that declare `hiddenOnMap`.
 */
function hiddenTypes(context: CompileContext): ReadonlySet<string> {
  return new Set(context.objectTypes.filter((type) => type.hiddenOnMap === true).map((type) => type.type));
}

/**
 * Lists the landmarks of one chunk: every behaviour prop whose feet point, in the chunk the server assigns the
 * entity (the chunk of the floor of the feet), falls in it, in map layer order and then prop order. A prop of a
 * hidden type, a prop driven by presence (which stays a prop of its layer) and a prop with no appearance are left
 * out. The point is the lowered feet in sixteenths of a tile.
 *
 * @param map The map.
 * @param context The compile context, for the hidden types.
 * @param appearances The world's appearance table.
 * @param tileSize Tile size in pixels.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @param chunkSize Chunk size in tiles.
 * @returns The chunk's landmarks, empty when there is none.
 */
export function chunkLandmarks(map: MapDocument, context: CompileContext, appearances: AppearanceTable, tileSize: number, cx: number, cy: number, chunkSize: number): ChunkLandmark[] {
  const hidden = hiddenTypes(context);
  const landmarks: ChunkLandmark[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      const behaviour = prop.behaviour;
      const appearance = appearances.indexOf.get(prop);
      if (behaviour === undefined || appearance === undefined || hidden.has(behaviour.type) || presenceIndex(prop, appearances) !== undefined) {
        continue;
      }
      const feet = loweredFeet(prop, appearances, appearance, tileSize);
      if (inChunk(Math.floor(feet.x), Math.floor(feet.y), cx, cy, chunkSize)) {
        landmarks.push({ appearance, x: Math.round(feet.x * SIXTEENTHS), y: Math.round(feet.y * SIXTEENTHS) });
      }
    }
  }
  return landmarks;
}
